import { localDate } from '../dates';
import { singletonId } from '../ids';
import type { Mutation } from '../mutations';
import type { BuildCtx } from '../spending/types';
import { isWeightUnit, weightMutation, type WeightResult, type WeightUnit } from './types';
import { isValidGrams } from './units';

export function weightSettingsId(userId: string): string {
  return singletonId(userId, 'weight_settings');
}

/**
 * Settings writes are partial upserts: an insert carrying only the fields being set (every other
 * column has a default or is nullable), so a device that hasn't pulled the row yet never
 * overwrites fields it didn't touch.
 */
export function buildSetWeightUnit(
  userId: string,
  unit: WeightUnit,
  ctx: BuildCtx,
): WeightResult<{ mutation: Mutation }, 'bad_unit'> {
  if (!isWeightUnit(unit)) return { ok: false, error: 'bad_unit' };
  return {
    ok: true,
    mutation: weightMutation(ctx, 'weight_settings', 'insert', weightSettingsId(userId), { unit }),
  };
}

/** Sets a goal; progress counts from `startG` (the current weight when the goal is set). */
export function buildSetWeightGoal(
  userId: string,
  input: { goalG: number; startG: number },
  ctx: BuildCtx,
): WeightResult<{ mutation: Mutation }, 'bad_weight' | 'same_as_now'> {
  if (!isValidGrams(input.goalG) || !isValidGrams(input.startG))
    return { ok: false, error: 'bad_weight' };
  if (input.goalG === input.startG) return { ok: false, error: 'same_as_now' };
  return {
    ok: true,
    mutation: weightMutation(ctx, 'weight_settings', 'insert', weightSettingsId(userId), {
      goalG: input.goalG,
      goalStartG: input.startG,
      goalStartDate: localDate(ctx.now, ctx.tz),
    }),
  };
}

export function buildClearWeightGoal(userId: string, ctx: BuildCtx): Mutation {
  return weightMutation(ctx, 'weight_settings', 'insert', weightSettingsId(userId), {
    goalG: null,
    goalStartG: null,
    goalStartDate: null,
  });
}
